import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Round a shared y-axis ceiling up to a round number with four even ticks (1,927 -> 2,000, so the
// ticks read 0 / 500 / 1,000 / 1,500 / 2,000 rather than 482 / 964 / ...).
export function niceCeiling(value) {
  const step = 10 ** Math.floor(Math.log10(value / 4))
  const nice = [1, 2, 2.5, 5, 10].map((m) => m * step).find((s) => s * 4 >= value)
  return nice * 4
}

export default class extends Controller {
  static values = {
    variant: String,
    data: Object,
    // Product Operations charts only: which view the Total | By person toggle is on ("total" is
    // the default), and -- on the charity care chart, which can break down by either of its two
    // metrics -- which metric the person lines measure.
    view: String,
    personMetric: String
  }

  static targets = ["chart", "viewButton", "personMetricButton", "personMetricSwitch"]

  connect() {
    this.renderChart()
  }

  disconnect() {
    this.destroyChart()
  }

  // Always clear the reference with the instance: a destroyed ApexCharts must never be destroyed
  // twice, and renderChart() can legitimately return without assigning a new one (the Product
  // Operations charts render an empty state, and no chart target, when nothing has matured).
  destroyChart() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  renderChart() {
    const data = this.dataValue
    // The Product Operations charts render an empty state instead of a canvas when no cohort in
    // range has matured, so the target can legitimately be absent.
    if (!data || !this.hasChartTarget) return

    if (this.variantValue === "stacked_bar") {
      this.renderStackedBar(data)
    } else if (this.variantValue === "line") {
      this.renderLine(data)
    } else if (this.variantValue === "total") {
      this.renderTotal(data)
    } else if (this.variantValue === "funnel") {
      this.renderFunnel(data)
    } else if (this.variantValue === "monthly_engagement") {
      this.renderMonthlyEngagement(data)
    } else if (this.variantValue === "product_operations_charity_care") {
      this.renderProductOperationsCharityCare(data)
    } else if (this.variantValue === "product_operations_document_retrieval") {
      this.renderProductOperationsDocumentRetrieval(data)
    } else if (this.variantValue === "product_operations_on_day") {
      this.renderProductOperationsOnDay(data)
    } else if (this.variantValue === "product_operations_volume") {
      this.renderProductOperationsVolume(data)
    }
  }

  switchToBar() {
    this.variantValue = "stacked_bar"
    this.updateToggleUI("bar")
    this.destroyChart()
    this.renderChart()
  }

  switchToLine() {
    this.variantValue = "line"
    this.updateToggleUI("line")
    this.destroyChart()
    this.renderChart()
  }

  switchToTotal() {
    this.variantValue = "total"
    this.updateToggleUI("total")
    this.destroyChart()
    this.renderChart()
  }

  // The toggle's LOOK is the `.segmented` button class; only which option is on belongs to the
  // controller, and that is `aria-pressed`. Rewriting className (what this used to do) dropped
  // `.segmented`, and a bare <button> falls straight back to the base purple pill chrome.
  updateToggleUI(active) {
    const buttons = {
      bar: this.element.querySelector(".moet-chart-toggle-bar"),
      line: this.element.querySelector(".moet-chart-toggle-line"),
      total: this.element.querySelector(".moet-chart-toggle-total")
    }

    Object.entries(buttons).forEach(([name, btn]) => {
      if (!btn) return
      btn.setAttribute("aria-pressed", String(name === active))
    })
  }

  renderStackedBar(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const options = {
      chart: {
        type: "bar",
        stacked: true,
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, (data.series || []).length),
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          borderRadiusWhenStacked: "last",
          columnWidth: "60%"
        }
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderLine(data) {
    const colors = [
      "#734CE0", "#E08C4C", "#4CADE0", "#E04C7A",
      "#4CE0A8", "#A84CE0", "#E0C84C", "#4C7AE0",
      "#E04CA8", "#7AE04C"
    ]

    const seriesCount = (data.series || []).length
    const dashPatterns = [0, 5, 8, 3, 10, 4, 6, 12, 7, 9]
    const markerShapes = ["circle", "square", "diamond", "triangle", "star",
                          "circle", "square", "diamond", "triangle", "star"]

    const options = {
      chart: {
        type: "line",
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: data.series || [],
      colors: colors.slice(0, seriesCount),
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray: dashPatterns.slice(0, seriesCount)
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: markerShapes.slice(0, seriesCount)
      },
      xaxis: {
        categories: data.weeks || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "right",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: markerShapes.slice(0, seriesCount) },
        itemMargin: { vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderTotal(data) {
    const weeks = data.weeks || []
    const seriesList = data.series || []
    const totals = weeks.map((_, i) =>
      seriesList.reduce((sum, s) => sum + ((s.data && s.data[i]) || 0), 0)
    )

    const options = {
      chart: {
        type: "line",
        height: 320,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: [{ name: "Total MOETs", data: totals }],
      colors: ["#734CE0"],
      stroke: { width: 3, curve: "smooth" },
      markers: { size: 5, strokeWidth: 0, shape: "circle" },
      xaxis: {
        categories: weeks,
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: { show: false },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => val.toLocaleString() }
      },
      dataLabels: { enabled: false }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderMonthlyEngagement(data) {
    const colors = ["#734CE0", "#E08C4C", "#4CADE0"]
    const dashPatterns = [0, 5, 8]
    const markerShapes = ["circle", "square", "diamond"]
    const series = data.series || []

    const options = {
      chart: {
        type: "line",
        height: 340,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: series,
      colors: colors.slice(0, series.length),
      stroke: {
        width: 3,
        curve: "smooth",
        dashArray: dashPatterns.slice(0, series.length)
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: markerShapes.slice(0, series.length)
      },
      xaxis: {
        categories: data.buckets || [],
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" },
          formatter: (val) => `${Math.round(val)}%`
        }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: markerShapes.slice(0, series.length) },
        itemMargin: { horizontal: 10, vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: { formatter: (val) => (val == null ? "—" : `${val}%`) }
      },
      dataLabels: { enabled: false }
    }

    const target = data.target
    if (target != null) {
      options.annotations = {
        yaxis: [
          {
            y: target,
            strokeDashArray: 0,
            borderColor: "#000000",
            borderWidth: 2,
            label: {
              text: `Target ${target}%`,
              borderColor: "#000000",
              position: "right",
              textAnchor: "end",
              style: { color: "#fff", background: "#000000", fontSize: "11px" }
            }
          }
        ]
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  renderFunnel(data) {
    const stages = ["Engaged", "Submitted", "Completed", "Approved"]
    const counts = [
      data.engaged || 0,
      data.submitted || 0,
      data.completed || 0,
      data.approved || 0
    ]
    // Step conversion: each stage as a % of the previous stage. The first
    // (Engaged) stage is shown as a % of all workable claims (data.total).
    const stepBase = (i) => (i === 0 ? (data.total || 0) : counts[i - 1])
    const stepLabel = (i) => (i === 0 ? "Total Claims" : stages[i - 1])
    const stepPct = (i) => {
      const base = stepBase(i)
      if (!base) return null
      return Math.round((counts[i] / base) * 1000) / 10
    }

    const options = {
      chart: {
        type: "bar",
        height: 200,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: [{ name: "Claims", data: counts }],
      colors: ["#734CE0"],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%"
        }
      },
      xaxis: {
        categories: stages,
        labels: { style: { colors: "#9CA3AF", fontSize: "11px" } }
      },
      yaxis: {
        labels: { style: { fontSize: "12px", colors: "#374151" } }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      dataLabels: {
        enabled: true,
        formatter: (val, opts) => {
          const pct = stepPct(opts.dataPointIndex)
          return pct == null ? val.toLocaleString() : `${val.toLocaleString()} (${pct}%)`
        },
        style: { fontSize: "12px", colors: ["#fff"] }
      },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const i = opts.dataPointIndex
            const pct = stepPct(i)
            return pct == null
              ? val.toLocaleString()
              : `${val.toLocaleString()} — ${pct}% of ${stepLabel(i)}`
          }
        }
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  // --- Product Operations dashboard ------------------------------------------------------
  //
  // Three cohort charts: "Charity care check-ins" (percentages only), "Document retrieval" (one
  // metric on two axes -- its percentage on the left, how long it took on the right) and "Tasks
  // completed on their available day" (one percentage). In all three the x axis is the cohort (the
  // week or month an item STARTED in), every series stops at the last cohort it has fully matured,
  // and an empty cohort is a null -- a gap, never a zero.
  //
  // Each chart can be switched between the team TOTAL and one line PER PERSON; both series sets
  // ride in the one payload, so the toggle is a re-render and never a request.
  //
  // Okabe-Ito hues come from the server (one per series) so the cards, the table and the charts
  // agree. Colour is never the only cue: each series also gets its own dash pattern and marker
  // shape, and each target is a faint dotted line in its series' own hue.
  //
  // In the Total view the dash/marker lists are indexed by the series' position, which the server
  // fixes: percentage series come first (solid, circle), day series after them (dashed, square).
  // A By person series instead carries its OWN `dash` and `marker` from the server, keyed to the
  // person rather than to their position in this particular chart's series list -- so one person
  // looks the same on all three charts even though they are not on all three.
  static PRODUCT_OPS_DASHES = [0, 6, 2, 9, 4]
  static PRODUCT_OPS_MARKERS = ["circle", "square", "diamond", "triangle", "star"]

  // The Total view plots a handful of series; By person plots up to 13, whose legend needs a row
  // or two more than the plot can spare.
  static PRODUCT_OPS_HEIGHT = 340
  static PRODUCT_OPS_PERSON_HEIGHT = 420

  // ApexCharts decides for itself whether the x labels need angling, but it measures the LAST
  // label rather than the widest one: `Utils.getLargestStringFromArr` reduces with `a.length >
  // b.length` over an array of NUMBERS (the category axis has been converted to a numeric one by
  // then), and `undefined > undefined` is false, so it just returns the final element. A spine
  // that happens to end on a narrow cohort ("Oct 5") is therefore judged to fit when forty of them
  // plainly do not, and the labels print flat on top of each other -- while the chart beside it,
  // ending on "Sep 21", is measured wide enough to angle its labels. Estimate it from the WIDEST
  // label instead. `rotateAlways: false` leaves ApexCharts' own check in place, so this only ever
  // turns rotation on, never off.
  static X_LABEL_CHAR_PX = 6 // about a character's width at the 11px label size
  static X_LABEL_FLAT_BUDGET_PX = 1000 // about the plot width these panels get in the page's grid

  xLabelsNeedRotating(categories) {
    const widest = categories.reduce((max, c) => Math.max(max, String(c).length), 0)
    return widest * this.constructor.X_LABEL_CHAR_PX * categories.length >
      this.constructor.X_LABEL_FLAT_BUDGET_PX
  }

  productOpsBaseOptions(series, categories) {
    const pick = (list, key) =>
      series.map((s, i) => (s[key] != null ? s[key] : list[i % list.length]))

    return {
      chart: {
        type: "line",
        height: this.constructor.PRODUCT_OPS_HEIGHT,
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      // The nulls are passed straight through: ApexCharts breaks the line at a null point, which is
      // exactly what an un-matured (or item-less) cohort should look like.
      series: series.map((s) => ({ name: s.name, data: s.data })),
      colors: series.map((s) => s.color),
      stroke: {
        width: 3,
        // Deliberately NOT "smooth": ApexCharts' spline overshoots its own data points, which on a
        // 0-100% axis draws a cohort above 100% or below 0%. Straight segments can't lie.
        curve: "straight",
        dashArray: pick(this.constructor.PRODUCT_OPS_DASHES, "dash")
      },
      markers: {
        size: 5,
        strokeWidth: 0,
        shape: pick(this.constructor.PRODUCT_OPS_MARKERS, "marker")
      },
      xaxis: {
        categories: categories,
        // One tick per cohort, said OUT LOUD. ApexCharts turns a category axis on a line chart into
        // a numeric one and defaults `tickAmount` to "dataPoints", but it only resolves that
        // default on a chart with MORE THAN ONE series (Range.js: `if (series.length > 1)`); a
        // single-series chart silently falls back to ApexCharts' 10 ticks. That is what broke the
        // on-day chart -- the only Product Operations chart that plots one line: 40 weekly cohorts
        // were drawn on a 10-tick axis, so the labels came out subsampled, unrotated and
        // overlapping while the two-series charts beside it were fine. An explicit count is the
        // same number ApexCharts computes for a multi-series chart, so all three charts' axes stay
        // identical whether they plot one line or several.
        tickAmount: Math.max(categories.length - 1, 0),
        labels: {
          rotate: -45,
          rotateAlways: this.xLabelsNeedRotating(categories),
          style: { colors: "#9CA3AF", fontSize: "11px" }
        },
        axisBorder: { color: "#E5E7EB" }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "top",
        horizontalAlign: "left",
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        markers: { shape: pick(this.constructor.PRODUCT_OPS_MARKERS, "marker") },
        itemMargin: { horizontal: 10, vertical: 4 }
      },
      dataLabels: { enabled: false }
    }
  }

  // --- Total | By person ------------------------------------------------------------------
  //
  // The Product Operations charts' version of the MOET dashboard's Stacked Bar | Line | Total
  // toggle, in the same component and the same classes. PERCENTAGES CANNOT BE STACKED, so the
  // choice is between the team's one line and one line per selected tasker. Who is included is
  // still the page's Taskers filter; this only chooses whether they are summed or drawn apart.

  // "total" (the default) or "person".
  productOpsView() {
    return this.viewValue === "person" ? "person" : "total"
  }

  // The By person group the metric switch currently points at. The charity care chart offers two
  // (13 people x 2 metrics on one panel is unreadable); the other two charts have a single group
  // and render no switch at all.
  productOpsPersonGroup(data) {
    const view = data.person_view || {}
    const groups = view.metrics || []
    const wanted = this.personMetricValue || view.default_metric
    return groups.find((g) => g.key === wanted) || groups[0] || null
  }

  switchChartView(event) {
    this.viewValue = event.params.view === "person" ? "person" : "total"
    this.updateProductOpsToggleUI()
    this.destroyChart()
    this.renderChart()
  }

  switchPersonMetric(event) {
    this.personMetricValue = event.params.metric
    this.updateProductOpsToggleUI()
    this.destroyChart()
    this.renderChart()
  }

  updateProductOpsToggleUI() {
    const person = this.productOpsView() === "person"
    this.markToggle(this.viewButtonTargets, "moetChartViewParam", person ? "person" : "total")

    // The metric switch only means anything once the lines are people, so it travels with the
    // view rather than sitting there inert beside the team line.
    // `hidden` and `flex` are both Tailwind display utilities, so both are toggled rather than
    // relying on which of them the generated stylesheet happens to emit last.
    if (this.hasPersonMetricSwitchTarget) {
      this.personMetricSwitchTarget.classList.toggle("hidden", !person)
      this.personMetricSwitchTarget.classList.toggle("flex", person)
    }
    if (this.hasPersonMetricButtonTarget) {
      const fallback = this.personMetricButtonTargets[0].dataset.moetChartMetricParam
      this.markToggle(this.personMetricButtonTargets, "moetChartMetricParam",
                      this.personMetricValue || fallback)
    }
  }

  // Same contract as the MOET toggle: `.segmented` owns the look, `aria-pressed` owns the state.
  // It is also what the selected option is announced by -- not a shade of grey alone.
  markToggle(buttons, datasetKey, active) {
    buttons.forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset[datasetKey] === active))
    })
  }

  // The cohort a point belongs to ("Week of Jun 1"), for the tooltip title.
  cohortTooltipX(data) {
    const labels = data.cohort_labels || []
    return { formatter: (val, opts) => labels[opts?.dataPointIndex] ?? val }
  }

  // Dotted target lines, captioned at the RIGHT end of the line. The legend is pinned to the top
  // LEFT of the plot, so a left-anchored caption lands underneath it; anchoring to the right edge
  // keeps every caption clear of it whatever the target is. Targets can also SHARE a y (the two
  // charity care goals are both 80%), which would print one caption on top of another -- so
  // same-y captions are stacked one text row at a time instead.
  //
  // `yAxisIndex` says which axis the y is measured on; it matters on the dual-axis document
  // retrieval chart, where 100 is a percentage on the left axis and 30 is days on the right one.
  // Each axis is annotated by its own call, so the same-y stacking never collides across axes.
  static TARGET_LABEL_OFFSET_X = -6
  static TARGET_LABEL_OFFSET_Y = -3
  static TARGET_LABEL_LINE_HEIGHT = 12

  productOpsTargetAnnotations(targets, yAxisIndex = 0) {
    const stacked = new Map()

    return targets.map(({ y, color, text }) => {
      const row = stacked.get(y) ?? 0
      stacked.set(y, row + 1)

      return {
        y: y,
        yAxisIndex: yAxisIndex,
        strokeDashArray: 4,
        borderColor: color,
        borderWidth: 1,
        opacity: 0.6,
        label: {
          text: text,
          position: "right",
          textAnchor: "end",
          offsetX: this.constructor.TARGET_LABEL_OFFSET_X,
          offsetY: this.constructor.TARGET_LABEL_OFFSET_Y -
            row * this.constructor.TARGET_LABEL_LINE_HEIGHT,
          borderColor: "transparent",
          style: { color: color, background: "transparent", fontSize: "10px" }
        }
      }
    })
  }

  // A 100% target sits exactly on the axis frame, where ApexCharts drops the annotation altogether
  // -- the user would see no line at all for it. Give the axis headroom whenever a target reaches
  // the top, keeping round 10% gridlines either way.
  percentAxisBounds(series) {
    const topTarget = Math.max(0, ...series.map((s) => s.target ?? 0))
    const max = topTarget >= 100 ? 110 : 100
    return { min: 0, max: max, tickAmount: max === 110 ? 11 : 5 }
  }

  // The days axis is scaled the same way: 10% of headroom over whichever is higher, the goal or the
  // slowest cohort, in steps of a round number of days so the goal ("30 days") lands exactly on a
  // tick of this axis (its labels are its own: with two axes they need not share the left axis'
  // gridlines).
  daysAxisBounds(series) {
    const goals = series.filter((s) => s.goal_days != null).map((s) => s.goal_days)
    const dataMax = Math.max(0, ...series.flatMap((s) => (s.data || []).filter((v) => v != null)))
    const top = Math.max(dataMax, ...goals) * 1.1
    if (top <= 0) return { min: 0 }

    // About six ticks, each a round multiple of 5 days.
    const step = Math.max(5, Math.ceil(top / 6 / 5) * 5)
    const max = Math.ceil(top / step) * step
    return { min: 0, max: max, tickAmount: max / step }
  }

  percentTooltipFormatter(series) {
    return (val, opts) => {
      if (val == null) return "\u2014"
      const counts = series[opts?.seriesIndex]?.counts?.[opts?.dataPointIndex]
      return counts ? `${val}% (${counts[0]}/${counts[1]})` : `${val}%`
    }
  }

  // `n` is how many items in that cohort could actually BE timed (the server's COUNT(days)), not
  // the cohort's size: one still waiting is a miss on the percentage line but contributes nothing
  // to the median/average.
  daysTooltipFormatter(series, stat) {
    return (val, opts) => {
      if (val == null) return "\u2014"
      const n = series[opts?.seriesIndex]?.sample_sizes?.[opts?.dataPointIndex]
      return n == null ? `${val} days (${stat})` : `${val} days (${stat}, n=${n})`
    }
  }

  // Every percent-only Product Operations chart: the charity care check-ins (two metrics on one
  // 0-100% axis), the tasks-done-on-their-day chart (one metric), and document retrieval once it
  // is switched to By person and loses its days axis.
  //
  // In the Total view each series carries its own target and is drawn in its own hue. By person,
  // the lines are people measured against ONE metric, so exactly one dotted target line is drawn,
  // in that metric's hue -- 13 identical target lines would be noise, not information.
  productOperationsPercentOptions(data) {
    const person = this.productOpsView() === "person"
    const group = person ? this.productOpsPersonGroup(data) : null
    const series = person ? ((group && group.series) || []) : (data.series || [])
    const options = this.productOpsBaseOptions(series, data.buckets || [])

    // Deliberately terse: the hue and the legend already say which metric a line belongs to, so
    // the caption only has to name the number it marks.
    const targets = person
      ? (group && group.target != null
        ? [{ y: group.target, color: group.target_color, text: `Target ${group.target}%` }]
        : [])
      : series
        .filter((s) => s.target != null)
        .map((s) => ({ y: s.target, color: s.color, text: `Target ${s.target}%` }))

    if (person) options.chart.height = this.constructor.PRODUCT_OPS_PERSON_HEIGHT

    options.yaxis = {
      ...this.percentAxisBounds(targets.map((t) => ({ target: t.y }))),
      labels: {
        style: { colors: "#9CA3AF", fontSize: "11px" },
        formatter: (val) => `${Math.round(val)}%`
      }
    }
    options.tooltip = {
      shared: true,
      intersect: false,
      x: this.cohortTooltipX(data),
      // `n` is small per person per week, so the counts matter more here than on the team line.
      y: { formatter: this.percentTooltipFormatter(series) }
    }
    options.annotations = { yaxis: this.productOpsTargetAnnotations(targets) }

    return options
  }

  // Chart 1: the two charity care check-in percentages, one 0-100% axis. By person it drops to one
  // of them at a time, chosen by the metric switch beside the view toggle.
  productOperationsCharityCareOptions(data) {
    return this.productOperationsPercentOptions(data)
  }

  // Chart 3: tasks finished on (or before) the Pacific day they became available.
  productOperationsOnDayOptions(data) {
    return this.productOperationsPercentOptions(data)
  }

  // Chart 2: document retrieval on TWO axes. The server tags every series with `axis` ("percent" or
  // "days"); the percentage line comes first, the day line after it.
  //
  // ApexCharts wants ONE yaxis entry per series. Entries that belong to the same axis are grouped
  // by pointing their `seriesName` at the same series, and only the first of them is drawn
  // (`show`), so series sharing an axis print one axis rather than one each.
  // Annotations then address an axis by its index in that array.
  static PERCENT_AXIS_TITLE = "% within 30 days"

  productOperationsDocumentRetrievalOptions(data) {
    // By person the days axis is dropped and only the percentage is plotted: a median-days line
    // per tasker would double 13 lines, and a person-week's timed sample is usually 0 or 1. The
    // days axis belongs to the Total view (docs/product_operations_dashboard.md).
    if (this.productOpsView() === "person") return this.productOperationsPercentOptions(data)

    const series = data.series || []
    const options = this.productOpsBaseOptions(series, data.buckets || [])
    const stat = data.stat === "average" ? "avg" : "median"
    const daysAxisTitle = data.stat === "average" ? "Average days" : "Median days"

    const isDays = (s) => s.axis === "days"
    const percentSeries = series.filter((s) => !isDays(s))
    const daysSeries = series.filter(isDays)
    const percentLead = series.findIndex((s) => !isDays(s))
    const daysLead = series.findIndex(isDays)
    const percentBounds = this.percentAxisBounds(percentSeries)
    const daysBounds = this.daysAxisBounds(daysSeries)
    const axisTitleStyle = { color: "#6B7280", fontSize: "11px", fontWeight: 500 }

    options.yaxis = series.map((s, index) => {
      const days = isDays(s)
      const lead = days ? daysLead : percentLead

      return {
        // Grouping key: every entry on an axis names that axis' FIRST series.
        seriesName: series[lead]?.name,
        opposite: days,
        show: index === lead,
        ...(days ? daysBounds : percentBounds),
        title: { text: days ? daysAxisTitle : this.constructor.PERCENT_AXIS_TITLE, style: axisTitleStyle },
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" },
          formatter: (val) => (days ? `${Math.round(val)}d` : `${Math.round(val)}%`)
        }
      }
    })

    options.annotations = {
      yaxis: [
        ...this.productOpsTargetAnnotations(
          percentSeries
            .filter((s) => s.target != null)
            .map((s) => ({ y: s.target, color: s.color, text: `Target ${s.target}%` })),
          percentLead
        ),
        ...this.productOpsTargetAnnotations(
          daysSeries
            .filter((s) => s.goal_days != null)
            .map((s) => ({ y: s.goal_days, color: s.color, text: `${s.goal_days} days` })),
          daysLead
        )
      ]
    }

    // One shared tooltip over both axes, so each row has to name its own unit and its own n.
    const percentFormatter = this.percentTooltipFormatter(series)
    const daysFormatter = this.daysTooltipFormatter(series, stat)
    options.tooltip = {
      shared: true,
      intersect: false,
      x: this.cohortTooltipX(data),
      y: {
        formatter: (val, opts) =>
          (isDays(series[opts?.seriesIndex] || {}) ? daysFormatter : percentFormatter)(val, opts)
      }
    }

    return options
  }

  renderProductOperationsCharityCare(data) {
    this.chart = new ApexCharts(this.chartTarget, this.productOperationsCharityCareOptions(data))
    this.chart.render()
  }

  renderProductOperationsDocumentRetrieval(data) {
    this.chart = new ApexCharts(this.chartTarget,
                                this.productOperationsDocumentRetrievalOptions(data))
    this.chart.render()
  }

  // The incoming task volume panels (calling / non-calling): a stacked bar per cohort, one segment
  // per task type, with the cohort's total in the tooltip. Counts, not percentages, so -- unlike
  // every other Product Operations chart -- stacking is exactly right here.
  productOperationsVolumeOptions(data) {
    const categories = data.buckets || []
    const totals = data.totals || []
    const labels = data.cohort_labels || []
    // The card's Stacked Bar | Line | Total switch (the MOET dashboard's three views): stacked bars
    // per type, one line per type, or one bar per period for the panel's total.
    const mode = ["bar", "line", "total"].includes(data.chart_type) ? data.chart_type : "bar"
    const series = mode === "total"
      ? [{ name: "Total", color: "#0072B2", data: totals }]
      : (data.series || [])
    // Too many bars for ApexCharts (the server decides, ProductOperationsQuery::VOLUME_MAX_BARS):
    // its bar renderer is quadratic in the bar count, so a stacked Day view of a long range froze
    // the page for seconds to minutes. Draw the same stack as filled areas -- one path per series.
    const asArea = mode !== "line" && data.as_area === true

    return {
      chart: {
        type: mode === "line" ? "line" : (asArea ? "area" : "bar"),
        stacked: mode === "bar",
        // A taller legend (every type, once "Other" is expanded) adds to the chart instead of
        // eating the plot area.
        height: 300 + Math.max(0, (data.legend_height || 84) - 84),
        fontFamily: "'Neue Haas Grotesk Text Pro', 'DM Sans', sans-serif",
        toolbar: { show: false }
      },
      series: series.map((s) => ({ name: s.name, data: s.data })),
      colors: series.map((s) => s.color),
      // Bars keep Apex's default outline, as before; lines get a 2px stroke and, while the points
      // are still far enough apart to tell apart, a dot per period.
      ...(mode === "line" ? { stroke: { width: 2, curve: "straight" }, markers: { size: categories.length <= 40 ? 3 : 0 } } : {}),
      // Solid fills (Apex's area default is a fading gradient, which would make a stacked band's
      // colour drift with its height) and a thin straight edge, so each band still reads as a series.
      ...(asArea ? { fill: { type: "solid", opacity: 0.85 }, stroke: { width: 1, curve: "straight" } } : {}),
      plotOptions: { bar: { columnWidth: "70%", borderRadius: 2, borderRadiusApplication: "end", borderRadiusWhenStacked: "last" } },
      xaxis: {
        categories: categories,
        // An area spans hundreds of periods; label about two dozen of them rather than letting
        // every date collide (bars keep one tick per bar).
        ...(asArea ? { tickAmount: Math.min(Math.max(categories.length - 1, 0), 24) } : {}),
        labels: {
          rotate: -45,
          rotateAlways: this.xLabelsNeedRotating(categories),
          hideOverlappingLabels: true,
          style: { colors: "#9CA3AF", fontSize: "11px" }
        },
        axisBorder: { color: "#E5E7EB" }
      },
      yaxis: {
        min: 0,
        // Shared with the neighbouring panel (calling | non-calling side by side) so bar heights
        // compare across the two; absent, Apex picks its own.
        ...(data.y_max > 0 ? { max: niceCeiling(data.y_max), tickAmount: 4 } : {}),
        forceNiceScale: true,
        labels: {
          style: { colors: "#9CA3AF", fontSize: "11px" },
          formatter: (val) => Math.round(val).toLocaleString()
        }
      },
      grid: { borderColor: "#F0F1F3", strokeDashArray: 3 },
      legend: {
        position: "top",
        horizontalAlign: "left",
        // A fixed legend height keeps the two side-by-side plot areas aligned even when one
        // legend wraps to more rows than the other.
        height: data.legend_height || 84,
        // Keep a one-type legend too (a panel narrowed to one picked type), both so the line is
        // labelled and so the two plots keep the same height. Total has nothing to name.
        show: mode !== "total",
        showForSingleSeries: true,
        fontSize: "12px",
        labels: { colors: "#6B7280" },
        itemMargin: { horizontal: 10, vertical: 4 }
      },
      tooltip: {
        shared: true,
        intersect: false,
        x: {
          formatter: (val, opts) => {
            const i = opts?.dataPointIndex
            const label = labels[i] ?? val
            return totals[i] != null ? `${label} \u2014 ${totals[i].toLocaleString()} tasks` : label
          }
        },
        y: { formatter: (val) => (val == null ? "\u2014" : val.toLocaleString()) }
      },
      dataLabels: { enabled: false }
    }
  }

  renderProductOperationsVolume(data) {
    this.chart = new ApexCharts(this.chartTarget, this.productOperationsVolumeOptions(data))
    this.chart.render()
  }

  renderProductOperationsOnDay(data) {
    this.chart = new ApexCharts(this.chartTarget, this.productOperationsOnDayOptions(data))
    this.chart.render()
  }
}
